import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { authHeaders } from "../utils/authHeaders";
import {
  FaThumbsUp,
  FaRegCommentDots,
  FaTrashAlt,
  FaUserCircle,
  FaPaperclip,
  FaTimes,
  FaAt,
  FaSearch,
  FaSyncAlt,
} from "react-icons/fa";
import "./ForumDiskusi.css";

export default function ForumDiskusi() {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [displayedPosts, setDisplayedPosts] = useState([]);
  const [content, setContent] = useState("");
  const [media, setMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);

  const [expandedComments, setExpandedComments] = useState({}); // postId -> bool
  const [commentsMap, setCommentsMap] = useState({});           // postId -> comments[]
  const [loadingComments, setLoadingComments] = useState({});   // postId -> bool

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [posting, setPosting] = useState(false);
  const [likingMap, setLikingMap] = useState({});
  const [deletingMap, setDeletingMap] = useState({});
  const composerRef = useRef(null);
  const mediaInputRef = useRef(null);

  const envApi = process.env.REACT_APP_API_URL || "/api";
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const isLocalWeb =
    typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
  const apiUrl = (() => {
    const raw = String(envApi || "").trim();
    if (!/^https?:\/\//i.test(raw)) return raw || "/api";
    try {
      const u = new URL(raw);
      const isLocalApi = /^(localhost|127\.0\.0\.1)$/i.test(u.hostname);
      if (!isLocalWeb && isLocalApi) return `${currentOrigin}/api`;
      if (typeof window !== "undefined" && window.location.protocol === "https:" && u.protocol === "http:") {
        return raw.replace(/^http:/i, "https:");
      }
      return raw;
    } catch {
      return raw || "/api";
    }
  })();
  const apiOrigin = apiUrl.startsWith("http")
    ? apiUrl.replace(/\/api\/?$/, "")
    : (currentOrigin || "");
  const uploadBase = apiOrigin || "/api";

  const resolveMediaUrl = (url) => {
    const raw = String(url || "").trim();
    if (!raw) return "";
    if (raw.startsWith("blob:")) return "";
    if (raw.startsWith("data:")) return raw;
    if (/^https?:\/\//i.test(raw)) {
      try {
        const u = new URL(raw);
        const isLocalHostUrl = /^(localhost|127\.0\.0\.1)$/i.test(u.hostname);
        if (isLocalHostUrl && u.pathname.startsWith("/uploads/")) {
          return `${currentOrigin}${u.pathname}`;
        }
        if (u.pathname.startsWith("/uploads/")) {
          return `${uploadBase}${u.pathname}`;
        }
      } catch {
        return raw;
      }
      return raw;
    }
    if (raw.startsWith("/uploads/")) return `${uploadBase}${raw}`;
    if (raw.startsWith("/")) return apiOrigin ? `${apiOrigin}${raw}` : raw;
    return apiOrigin ? `${apiOrigin}/${raw}` : raw;
  };

  const isVideoUrl = (url = "") => /\.(mp4|webm|ogg|mov|m4v)$/i.test(String(url));

  const resizeComposer = () => {
    const el = composerRef.current;
    if (!el) return;
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 20;
    const minHeight = Math.ceil(lineHeight);
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  };

  const clearMediaSelection = () => {
    if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    setMedia(null);
    setMediaPreview(null);
    if (mediaInputRef.current) mediaInputRef.current.value = "";
  };

  // ---------- Load posts
  useEffect(() => {
    axios
      .get(`${apiUrl}/forum`)
      .then((res) => {
        const list = res?.data?.posts ?? res?.data?.data ?? [];
        if (Array.isArray(list)) setPosts(list);
      })
      .catch((err) => console.error("Gagal ambil postingan:", err));
  }, [apiUrl]);

  // ---------- Filter window (7 hari) + pencarian
  useEffect(() => {
    const now = new Date();
    const isFiltered = searchQuery || dateFilter;

    const filtered = posts.filter((p) => {
      const nameMatch = (p.user_name || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const dateMatch = dateFilter ? String(p.timestamp).startsWith(dateFilter) : true;
      const daysDiff = (now - new Date(p.timestamp)) / (1000 * 60 * 60 * 24);
      const within7 = daysDiff <= 7;
      return nameMatch && dateMatch && (showAllPosts || isFiltered || within7);
    });

    setDisplayedPosts(filtered);
  }, [posts, searchQuery, dateFilter, showAllPosts]);

  useEffect(() => {
    resizeComposer();
  }, [content]);

  // ---------- Helpers: fetch comments (kompatibel 2 endpoint)
  async function fetchComments(postId) {
    try {
      const r = await axios.get(`${apiUrl}/forum/comments`, { params: { post_id: postId } });
      const items = r?.data?.comments ?? r?.data?.data ?? [];
      return Array.isArray(items) ? items : [];
    } catch {
      try {
        const r2 = await axios.get(`${apiUrl}/forum/comments/${postId}`);
        const items2 = r2?.data?.comments ?? r2?.data?.data ?? [];
        return Array.isArray(items2) ? items2 : [];
      } catch (err) {
        console.error("fetchComments error", err);
        return [];
      }
    }
  }

  const toggleComments = async (postId) => {
    setExpandedComments((prev) => ({ ...prev, [postId]: !prev[postId] }));

    // Muat komentar pertama kali saat dibuka
    if (!commentsMap[postId]) {
      setLoadingComments((prev) => ({ ...prev, [postId]: true }));
      const items = await fetchComments(postId);
      setCommentsMap((prev) => ({ ...prev, [postId]: items }));
      setLoadingComments((prev) => ({ ...prev, [postId]: false }));
    }
  };

  // ---------- Posting
  const handlePost = async () => {
    if ((!content.trim() && !media) || posting) return;  // boleh teks ATAU media
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("user_id", user.id);
      fd.append("user_name", user.full_name);
      fd.append("content", content);
      if (media) fd.append("media", media);

      const res = await axios.post(`${apiUrl}/forum/add`, fd, {
        headers: authHeaders(user, { "Content-Type": "multipart/form-data" }),
      });

      if (res?.data?.success) {
        const serverPost = res?.data?.post ?? res?.data?.data;
        const newPost =
          serverPost || {
            id: Date.now(),
            user_id: user.id,
            user_name: user.full_name,
            content,
            media_url: null,
            timestamp: new Date().toISOString(),
            likes: 0,
            comments_count: 0
          };

        setPosts((prev) => [newPost, ...prev]);
        setContent("");
        clearMediaSelection();
      } else {
        alert(res?.data?.message || "Gagal posting");
      }
    } catch {
      alert("Gagal posting (server error)");
    } finally {
      setPosting(false);
    }
  };

  // ---------- Komentar
  const handleComment = async (postId, txt, parentId = null) => {
    if (!txt.trim()) return;
    try {
      const res = await axios.post(`${apiUrl}/forum/comments`, {
        post_id: postId,
        user_id: user.id,
        user_name: user.full_name,
        content: txt,
        parent_id: parentId,
      }, {
        headers: authHeaders(user),
      });
      if (res?.data?.success) {
        const comment = res?.data?.comment ?? res?.data?.data;

        // Tambahkan ke commentsMap post tsb
        setCommentsMap((prev) => {
          const cur = prev[postId] || [];
          return { ...prev, [postId]: [...cur, comment] };
        });

        // Naikkan counter di list post agar jumlah komentar ikut naik.
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  comments_count:
                    typeof p.comments_count === "number"
                      ? p.comments_count + 1
                      : (commentsMap[postId]?.length || 0) + 1,
                }
              : p
          )
        );
      }
    } catch {
      console.error("Gagal komentar");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDateFilter("");
  };

  const handleLike = async (postId) => {
    if (!postId || likingMap[postId]) return;
    setLikingMap((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await axios.post(`${apiUrl}/forum/${postId}/like`);
      if (res?.data?.success) {
        const likesFromServer = Number(res?.data?.likes);
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  likes: Number.isFinite(likesFromServer)
                    ? likesFromServer
                    : (Number(p.likes) || 0) + 1,
                }
              : p
          )
        );
      }
    } catch (err) {
      console.error("Gagal like post:", err);
    } finally {
      setLikingMap((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeletePost = async (postId) => {
    if (!postId || deletingMap[postId]) return;
    const ok = window.confirm("Hapus postingan ini?");
    if (!ok) return;

    setDeletingMap((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await axios.delete(`${apiUrl}/forum/${postId}`, {
        data: { user_id: user?.id ?? null },
        headers: authHeaders(user),
      });
      if (res?.data?.success) {
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        setCommentsMap((prev) => {
          const next = { ...prev };
          delete next[postId];
          return next;
        });
        setExpandedComments((prev) => {
          const next = { ...prev };
          delete next[postId];
          return next;
        });
      } else {
        alert(res?.data?.message || "Gagal menghapus postingan");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menghapus postingan");
    } finally {
      setDeletingMap((prev) => ({ ...prev, [postId]: false }));
    }
  };

  // ---------- Komponen kecil
  const ReplyForm = ({ postId, parentId }) => {
    const [txt, setTxt] = useState("");
    return (
      <div className="reply-form">
        <input
          value={txt}
          onChange={(e) => setTxt(e.target.value)}
          placeholder="Balas komentar..."
        />
        <button
          onClick={() => {
            handleComment(postId, txt, parentId);
            setTxt("");
          }}
        >
          Kirim
        </button>
      </div>
    );
  };

  const renderComments = (comments, postId, parent = null, lvl = 0) =>
    comments
      .filter((c) => c.parent_id === parent)
      .map((c) => (
        <div
          key={c.id}
          className="comment-item"
          style={{ marginLeft: lvl * 20 }}
        >
          <div className="comment-user">
            <FaUserCircle /> <strong>{c.user_name}</strong>
          </div>
          <div className="comment-text">{c.content}</div>
          <div className="timestamp">
            {new Date(c.timestamp).toLocaleString()}
          </div>
          <ReplyForm postId={postId} parentId={c.id} />
          {renderComments(comments, postId, c.id, lvl + 1)}
        </div>
      ));

  // ---------- Render
  return (
    <div className="forum-container">
      <div className="forum-header">
        <h2>Forum Diskusi Sahabat Kakao</h2>
        <div className="forum-filters">
          <div className="search-bar">
            <FaSearch />
            <input
              placeholder="Cari nama user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <input
            type="date"
            className="date-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
          <button
            className="clear-filters"
            onClick={clearFilters}
            title="Clear Search & Date"
          >
            <FaSyncAlt /> Clear Filter
          </button>
        </div>
      </div>

      {/* Composer (tombol Posting di dalam area) */}
      <div className="post-form card">
        <div className="textarea-container">
          <textarea
            ref={composerRef}
            placeholder="Apa yang ingin Anda bagikan?"
            value={content}
            style={{ height: "1.4em" }}
            onChange={(e) => {
              setContent(e.target.value);
              resizeComposer();
            }}
            rows={1}
          />

          {mediaPreview && (
            <div className="media-preview media-preview-inline">
              <button
                type="button"
                className="media-remove-btn"
                onClick={clearMediaSelection}
                title="Hapus lampiran"
                aria-label="Hapus lampiran"
              >
                <FaTimes />
              </button>
              {media?.type?.startsWith("video/")
                ? <video controls src={mediaPreview} />
                : <img src={mediaPreview} alt="preview" />
              }
            </div>
          )}

          <div className="textarea-icons">
            <label className="icon-btn" title="Lampirkan media">
              <FaPaperclip />
              <input
                ref={mediaInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setMedia(f || null);
                  if (mediaPreview) URL.revokeObjectURL(mediaPreview);
                  setMediaPreview(f ? URL.createObjectURL(f) : null);
                }}
              />
            </label>

            <button type="button" className="icon-btn" title="Mention User">
              <FaAt />
            </button>

            <button
              type="button"
              className="btn-post-inline"
              onClick={handlePost}
              disabled={posting || (!content.trim() && !media)}
            >
              {posting ? "Posting..." : "Posting"}
            </button>
          </div>
        </div>
      </div>

      {displayedPosts.map((p) => {
        const loadedComments = commentsMap[p.id];
        const bubbleCount =
          typeof p.comments_count === "number"
            ? p.comments_count
            : Array.isArray(loadedComments)
            ? loadedComments.length
            : 0;

        return (
          <div key={p.id} className="post-item card">
            <div className="post-header">
              <FaUserCircle className="profile-icon" />
              <strong>{p.user_name}</strong>
            </div>

            <div className="post-content">{p.content}</div>

            {p.media_url && (
              isVideoUrl(p.media_url) ? (
                <video controls className="post-media" src={resolveMediaUrl(p.media_url)} />
              ) : (
                <img className="post-media" src={resolveMediaUrl(p.media_url)} alt="" />
              )
            )}

            <div className="post-footer">
              <div className="timestamp">
                {new Date(p.timestamp).toLocaleString()}
              </div>
              <div className="post-actions">
                <button
                  type="button"
                  onClick={() => handleLike(p.id)}
                  disabled={!!likingMap[p.id]}
                >
                  <FaThumbsUp /> {p.likes || 0}
                </button>
                <button type="button" onClick={() => toggleComments(p.id)}>
                  <FaRegCommentDots /> {bubbleCount}
                </button>
                <button
                  type="button"
                  className="post-delete-btn"
                  onClick={() => handleDeletePost(p.id)}
                  disabled={!!deletingMap[p.id]}
                  title="Hapus postingan"
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>

            {expandedComments[p.id] && (
              <div className="comment-section">
                {loadingComments[p.id] ? (
                  <div style={{ fontSize: 12, color: "#777" }}>Memuat komentar...</div>
                ) : (
                  <>
                    <ReplyForm postId={p.id} parentId={null} />
                    <div className="comment-list">
                      {renderComments(loadedComments || [], p.id)}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!showAllPosts && posts.length > displayedPosts.length && (
        <div className="see-more">
          <button onClick={() => setShowAllPosts(true)}>
            Lihat Postingan Lama
          </button>
        </div>
      )}
    </div>
  );
}

